import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ornament } from "./Ornament";

/**
 * "Our Performers" — editorial 3×2 grid of Signature Acts.
 * Dark black backdrop, gold rim-lit portraits, cinematic reveal on scroll.
 */
const ACTS = [
  {
    id: "golden-performer",
    name: "Golden Performer",
    image: "/generated/act-golden-performer.jpg",
    tagline: "The Radiant Sun",
  },
  {
    id: "aerial-duo",
    name: "Aerial Duo",
    image: "/generated/act-aerial-duo.jpg",
    tagline: "Ballet in the Air",
  },
  {
    id: "fire-performance",
    name: "Fire Performance",
    image: "/generated/act-fire-performance.jpg",
    tagline: "Ritual of Flames",
  },
  {
    id: "oriental-show",
    name: "Oriental Show",
    image: "/generated/act-oriental-show.jpg",
    tagline: "Whispers of the East",
  },
  {
    id: "tribal-dance",
    name: "Tribal Dance",
    image: "/generated/act-tribal-dance.jpg",
    tagline: "Primal Rhythm",
  },
  {
    id: "live-music-band",
    name: "Live Music Band",
    image: "/generated/act-live-music-band.jpg",
    tagline: "Sound & Soul",
  },
];

export default function OurPerformers() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="our-performers"
      className="relative bg-[var(--lu-black)] grain overflow-hidden"
      data-testid="our-performers-section"
    >
      <div className="container-lux py-24 lg:py-32">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="kicker text-[var(--lu-gold)]">Signature Acts</p>
          <h2 className="mt-6 h-editorial text-[42px] sm:text-[58px] lg:text-[86px] text-[var(--lu-ivory)] leading-[0.98]">
            Our <span className="italic text-[var(--lu-gold)]">performers</span>
          </h2>
          <Ornament className="my-10" />
          <p className="text-[15px] leading-[1.9] text-ivory-75 font-light">
            A curated roster of world-class performers — each act a
            small story of spectacle. Hand-picked performers who bring
            the magic to your night.
          </p>
        </div>

        {/* 3×2 grid */}
        <div
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7"
          data-testid="performers-grid"
        >
          {ACTS.map((act, i) => (
            <article
              key={act.id}
              className={`group relative overflow-hidden border border-[var(--lu-gold)]/30 hover:border-[var(--lu-gold)]/70 rounded-sm cursor-pointer transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{
                aspectRatio: "3 / 4",
                transitionDelay: `${i * 110}ms`,
              }}
              onClick={() => nav("/services")}
              data-testid={`performer-card-${act.id}`}
            >
              <img
                src={act.image}
                alt={`${act.name} — Lumière Signature Act`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
              />

              {/* Dark gradient for text legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />

              {/* Gold hairline frame on hover */}
              <div className="absolute inset-3 border border-[var(--lu-gold)]/0 group-hover:border-[var(--lu-gold)]/40 transition-all duration-500" />

              {/* Text overlay */}
              <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="font-micro text-[10px] tracking-[0.32em] uppercase text-[var(--lu-gold)]/80">
                  {act.tagline}
                </p>
                <h3 className="mt-2 h-editorial text-[30px] lg:text-[36px] leading-[1.05] text-[var(--lu-ivory)]">
                  {act.name}
                </h3>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
